import { getAuthenticatedAxios } from "../utils/api";
import { extractPmidFromUrl } from "../shared/utils/paper";

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 25;

let eventQueue = [];
let flushTimer = null;
let isFlushing = false;

const createClientEventId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const scheduleFlush = () => {
  if (flushTimer || typeof window === "undefined") return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushResultEngagementEvents();
  }, FLUSH_INTERVAL_MS);
};

const normalizePaperPayload = (paper = {}, index = 0) => {
  const pmid = paper.pmid || paper.PMID || extractPmidFromUrl(paper.url);
  const rank = paper.rank || paper.Rank || paper.ranking || paper.Ranking || index + 1;
  const relevanceScore = paper.relevance_score ?? paper.RelevanceScore;

  return {
    pmid: pmid || undefined,
    title: paper.title || paper.Title || undefined,
    paper_url: paper.url || paper.URL || undefined,
    rank: Number.isFinite(Number(rank)) ? Number(rank) : undefined,
    has_full_text: Boolean(paper.pmcid || paper.PMCID),
    pmcid: paper.pmcid || paper.PMCID || undefined,
    relevance_score:
      relevanceScore !== undefined && Number.isFinite(Number(relevanceScore))
        ? Number(relevanceScore)
        : undefined,
  };
};

export const buildPaperEngagementEvent = ({
  eventType,
  paper,
  index = 0,
  searchId,
  query,
  searchMode,
  surface,
  metadata,
  visibleDurationMs,
  maxVisibleRatio,
}) => ({
  event_type: eventType,
  search_id: searchId || undefined,
  query: query || undefined,
  search_mode: searchMode || undefined,
  surface,
  ...normalizePaperPayload(paper, index),
  visible_duration_ms: visibleDurationMs,
  max_visible_ratio: maxVisibleRatio,
  metadata,
});

export const buildPaperAnalysisEngagementEvent = ({
  eventType,
  searchId,
  query,
  searchMode,
  message,
  feedbackText,
}) => {
  const sequence = Number(message?.sequence);
  if (!Number.isInteger(sequence) || sequence < 1) {
    return null;
  }

  return {
    event_type: eventType,
    search_id: searchId || undefined,
    query: query || undefined,
    search_mode: searchMode || undefined,
    surface: "paper_analysis",
    metadata: {
      paper_analysis_message_sequence: sequence,
      message_created_at: message?.createdAt || undefined,
      feedback_text: feedbackText || undefined,
    },
  };
};

export const buildSearchHistoryOpenEngagementEvent = ({
  searchDetails,
  surface,
  entryPoint,
}) => {
  const searchId = searchDetails?.id || searchDetails?.search_id;
  if (!searchId) return null;

  return {
    event_type: "search_history_open",
    search_id: searchId,
    query: searchDetails?.query || undefined,
    search_mode: searchDetails?.search_mode || searchDetails?.search_type || undefined,
    surface,
    metadata: {
      entry_point: entryPoint,
      result_count: Array.isArray(searchDetails?.papers)
        ? searchDetails.papers.length
        : searchDetails?.result_count,
      created_at: searchDetails?.created_at,
    },
  };
};

export const enqueueResultEngagementEvent = (event) => {
  if (!event || !event.event_type) return;
  eventQueue.push({
    ...event,
    client_event_id: event.client_event_id || createClientEventId(),
    timestamp: event.timestamp || new Date().toISOString(),
  });

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushResultEngagementEvents();
    return;
  }
  scheduleFlush();
};

export const flushResultEngagementEvents = async () => {
  if (isFlushing || eventQueue.length === 0) return;

  const batch = eventQueue.slice(0, MAX_BATCH_SIZE);
  eventQueue = eventQueue.slice(MAX_BATCH_SIZE);
  isFlushing = true;

  try {
    const axios = getAuthenticatedAxios();
    await axios.post("/api/v1/result-engagement/events", { events: batch });
  } catch (error) {
    console.warn("Failed to record result engagement events:", error);
  } finally {
    isFlushing = false;
    if (eventQueue.length > 0) {
      scheduleFlush();
    }
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushResultEngagementEvents();
    }
  });
  window.addEventListener("beforeunload", () => {
    flushResultEngagementEvents();
  });
}

const resultEngagementService = {
  buildPaperEngagementEvent,
  buildPaperAnalysisEngagementEvent,
  buildSearchHistoryOpenEngagementEvent,
  enqueueResultEngagementEvent,
  flushResultEngagementEvents,
};

export default resultEngagementService;
